import { icons } from "./icons";

export type FormatCommand =
  | "undo"
  | "redo"
  | "formatBlock"
  | "bold"
  | "italic"
  | "underline"
  | "strikeThrough"
  | "justifyLeft"
  | "justifyCenter"
  | "justifyRight"
  | "insertUnorderedList"
  | "insertOrderedList"
  | "createLink"
  | "insertImage"
  | "insertHorizontalRule"
  | "removeFormat";

interface ToolSpec {
  cmd: FormatCommand;
  title: string;
  icon?: string;
  value?: string;
  state?: string;
}

const GROUPS: ToolSpec[][] = [
  [
    { cmd: "undo", title: "Undo", icon: icons.undo },
    { cmd: "redo", title: "Redo", icon: icons.redo },
  ],
  [
    { cmd: "formatBlock", title: "Paragraph", value: "p" },
    { cmd: "formatBlock", title: "Heading 1", value: "h1" },
    { cmd: "formatBlock", title: "Heading 2", value: "h2" },
    { cmd: "formatBlock", title: "Heading 3", value: "h3" },
  ],
  [
    { cmd: "bold", title: "Bold", icon: icons.bold, state: "bold" },
    { cmd: "italic", title: "Italic", icon: icons.italic, state: "italic" },
    { cmd: "underline", title: "Underline", icon: icons.underline, state: "underline" },
    { cmd: "strikeThrough", title: "Strikethrough", icon: icons.strike, state: "strikeThrough" },
  ],
  [
    { cmd: "justifyLeft", title: "Align left", icon: icons.alignLeft, state: "justifyLeft" },
    { cmd: "justifyCenter", title: "Align center", icon: icons.alignCenter, state: "justifyCenter" },
    { cmd: "justifyRight", title: "Align right", icon: icons.alignRight, state: "justifyRight" },
  ],
  [
    { cmd: "insertUnorderedList", title: "Bullet list", icon: icons.listUl, state: "insertUnorderedList" },
    { cmd: "insertOrderedList", title: "Numbered list", icon: icons.listOl, state: "insertOrderedList" },
  ],
  [
    { cmd: "createLink", title: "Link", icon: icons.link },
    { cmd: "insertImage", title: "Image", icon: icons.image },
    { cmd: "insertHorizontalRule", title: "Horizontal rule", icon: icons.rule },
    { cmd: "removeFormat", title: "Clear formatting", icon: icons.erase },
  ],
];

function queryState(command: string): boolean {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

function queryBlock(): string {
  try {
    return document.queryCommandValue("formatBlock").toLowerCase();
  } catch {
    return "";
  }
}

export function runFormat(canvas: HTMLElement, spec: ToolSpec): void {
  canvas.focus();

  if (spec.cmd === "createLink") {
    const url = window.prompt("Link URL", "https://");
    if (url && /^(https?:|mailto:|tel:|\/|#)/i.test(url.trim())) document.execCommand("createLink", false, url);
    return;
  }

  if (spec.cmd === "insertImage") {
    const url = window.prompt("Image URL", "https://");
    if (url && /^(https?:|mailto:|tel:|\/|#)/i.test(url.trim())) document.execCommand("insertImage", false, url);
    return;
  }

  if (spec.cmd === "formatBlock") {
    document.execCommand("formatBlock", false, spec.value ?? "p");
    return;
  }

  document.execCommand(spec.cmd, false);
}

export function createToolbar(
  canvas: HTMLElement,
  onEdit: () => void,
): HTMLElement {
  const bar = document.createElement("div");
  bar.className = "hdp-format";
  bar.setAttribute("role", "toolbar");
  bar.setAttribute("aria-label", "Formatting");

  for (const group of GROUPS) {
    const wrap = document.createElement("div");
    wrap.className = "hdp-format__group";
    for (const spec of group) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = spec.icon ? "hdp-format__btn" : "hdp-format__btn hdp-format__btn--text";
      btn.title = spec.title;
      btn.dataset.cmd = spec.cmd;
      if (spec.value) btn.dataset.value = spec.value;
      if (spec.state) btn.dataset.state = spec.state;
      btn.innerHTML = spec.icon ?? spec.value?.toUpperCase() ?? spec.title;
      btn.addEventListener("mousedown", (e) => e.preventDefault());
      btn.addEventListener("click", () => {
        runFormat(canvas, spec);
        refreshToolbar(bar);
        onEdit();
      });
      wrap.append(btn);
    }
    bar.append(wrap);
  }

  return bar;
}

export function refreshToolbar(bar: HTMLElement): void {
  const block = queryBlock();
  for (const btn of bar.querySelectorAll<HTMLButtonElement>("button")) {
    const state = btn.dataset.state;
    const value = btn.dataset.value;
    const cmd = btn.dataset.cmd;
    const on =
      cmd === "formatBlock" && value
        ? block === value || block === `<${value}>`
        : state
          ? queryState(state)
          : false;
    btn.classList.toggle("is-on", on);
  }
}
